import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UtensilsCrossed,
  Coffee,
  Store,
  Factory,
  ArrowUpRight,
} from 'lucide-react';
import {
  homeScrollItem,
  homeScrollStagger,
  homeScrollViewport,
} from '../../utils/homeScrollMotion';
import styles from './IndustriesSection.module.css';

const sectors = [
  {
    id: 'food',
    icon: UtensilsCrossed,
    num: '01',
    name: 'Food Service & QSR',
    image: '/images/products/Food-Wrap-Paper.jpeg',
    link: '/products/food-wrap-paper',
    summary: 'Wraps, plates & takeaway packs for repeat dispatch.',
    paragraph:
      'Food-grade wraps, plates and takeaway packs built for high-volume kitchens. Grease-resistant sheets, consistent GSM and repeat bulk dispatch across India.',
    clients: ['Restaurants & cafés', 'Cloud kitchens', 'QSR chains', 'Catering'],
    hue: 'green',
  },
  {
    id: 'beverage',
    icon: Coffee,
    num: '02',
    name: 'Beverage & Café',
    image: '/images/products/Paper-Cup-Sleeves.jpeg',
    link: '/products/paper-cup-sleeves',
    summary: 'Cup sleeves with smooth print face for hot cups.',
    paragraph:
      'Branded cup sleeves with a smooth print surface for hot and cold cups. Custom sizes, sharp colour reproduction and reliable supply for café and franchise roll-outs.',
    clients: ['Coffee shops', 'Juice bars', 'Franchise brands', 'Corporate'],
    hue: 'coral',
  },
  {
    id: 'retail',
    icon: Store,
    num: '03',
    name: 'Retail & E-commerce',
    image: '/images/products/packaging1 (1).png',
    link: '/products/packaging',
    summary: 'Shelf-ready retail packs & presentation papers.',
    paragraph:
      'Shelf-ready retail packs and presentation papers for brands that need sharp print, neat folds and dispatch-ready cartons. Ideal for D2C, retail and gift packaging programs.',
    clients: ['Retail brands', 'D2C sellers', '3PL partners', 'Gift packs'],
    hue: 'mint',
  },
  {
    id: 'industrial',
    icon: Factory,
    num: '04',
    name: 'Industrial Converting',
    image: '/images/products/bobbins.jpeg',
    link: '/products/pe-coated-rolls',
    summary: 'PE coated rolls, bobbins & jumbo stock for lines.',
    paragraph:
      'PE coated rolls, bobbins and jumbo stock for converting lines and packers. Stable GSM, clean unwind and bulk contracts for distributors and industrial buyers pan-India.',
    clients: ['Converters', 'Packers', 'Distributors', 'Bulk contracts'],
    hue: 'sun',
  },
];

export default function IndustriesSection() {
  return (
    <section
      className={`${styles.section} home-bg-sage`}
      aria-labelledby="industries-heading"
    >
      <div className={styles.bgDecor} aria-hidden="true">
        <span className={styles.orbGreen} />
        <span className={styles.orbOrange} />
      </div>

      <div className={styles.container}>
        <motion.header
          className={styles.header}
          variants={homeScrollItem}
          initial="hidden"
          whileInView="visible"
          viewport={homeScrollViewport}
        >
          <h2 id="industries-heading" className={styles.title}>
            Industries <span className={styles.highlight}>We Serve</span>
          </h2>
          <p className={styles.subtitle}>
            Four markets · pan-India supply from Meerut
          </p>
        </motion.header>

        <motion.div
          className={styles.grid}
          variants={homeScrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={homeScrollViewport}
        >
          {sectors.map((sector) => {
            const Icon = sector.icon;
            return (
              <motion.article
                key={sector.id}
                className={styles.card}
                data-hue={sector.hue}
                variants={homeScrollItem}
              >
                <span className={styles.cardGlow} aria-hidden="true" />
                <span className={styles.cardBar} aria-hidden="true" />

                <div className={styles.cardMedia}>
                  <img src={sector.image} alt={sector.name} loading="lazy" />
                  <span className={styles.mediaShade} aria-hidden="true" />
                  <span className={styles.numTag}>{sector.num}</span>
                  <span className={styles.iconTag}>
                    <Icon size={17} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                </div>

                <div className={styles.cardBody}>
                  <h3 className={styles.cardTitle}>{sector.name}</h3>
                  <p className={styles.summary}>{sector.summary}</p>
                  <p className={styles.paragraph}>{sector.paragraph}</p>

                  <div className={styles.clientsPanel}>
                    <span className={styles.clientsLabel}>Who we supply</span>
                    <ul
                      className={styles.clients}
                      aria-label={`Who we supply — ${sector.name}`}
                    >
                      {sector.clients.map((client) => (
                        <li key={client}>
                          <span className={styles.clientDot} aria-hidden="true" />
                          <span className={styles.clientText}>{client}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={sector.link} className={styles.cta}>
                    View range
                    <ArrowUpRight size={11} strokeWidth={2.5} aria-hidden="true" />
                  </Link>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
